import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Collapse,
  Modal,
  Spin,
  Table,
  Descriptions,
  Divider,
  Progress,
  Card,
  Alert,
  Empty
} from 'antd';
import FurtherInformation, { renderTwoColumnKV } from './FurtherInformation';
import styles from './index.scss';
import { getRelatedPartyInfo } from '../../../../../services/ns/runInstance';

import {
  capitalize,
  isArray,
  isPlainObject,
  startCase
} from '../../../../../util/lodashlike';
import { renderBooleanValue } from '../../../../../util/miscellaneousUtils';

const { Panel } = Collapse;
const { Item } = Descriptions;

const COLUMNS = [
  {
    title: 'Associate Entity',
    dataIndex: 'id',
    key: 'id'
  },
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name'
  },
  {
    title: 'Relationship',
    dataIndex: 'relation',
    key: 'relation'
  },
  {
    title: 'Category',
    dataIndex: 'category',
    key: 'category'
  }
];

const formatAddress = (addressObj) => {
  if (!addressObj) return '';

  try {
    const parsedAddress = JSON.parse(addressObj);

    if (typeof parsedAddress !== 'object') return parsedAddress;

    return Object.values(parsedAddress)
      .filter((val) => val && String(val).trim() !== '')
      .join(', ');
  } catch (e) {
    return addressObj;
  }
};

const parseJsonString = (data) => {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch (e) {
      return data;
    }
  }
  return data;
};

const getDatatype = (parsedData) => {
  if (isPlainObject(parsedData)) return 'object';
  if (isArray(parsedData)) return 'array';
  return 'string';
};

const renderObject = (data, title, className) => (
  <Card key={`${title}-object}`} className="objectCard" bodyStyle={undefined}>
    {title && <div className={styles.blockTitle}>{title}</div>}
    {renderTwoColumnKV(data, className)}
  </Card>
);

const renderTable = (data, title) => {
  let columns = [];

  if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
    columns = Object.keys(data[0]).map((key) => ({
      title: startCase(key),
      dataIndex: key,
      key: key
    }));
  }

  return data?.length > 0 ? (
    <Card key={`${title}-table}`} className="tableCard" bodyStyle={undefined}>
      {title && <div className={styles.blockTitle}>{title}</div>}
      <Table
        columns={columns}
        dataSource={data || []}
        pagination={false}
        size="small"
      />
    </Card>
  ) : (
    <> </>
  );
};

const RelatedPartyModal = ({ visible, onCancel, hitDetails, watchlistId }) => {
  const [relatedPartyDetails, setRelatedPartyDetails] = useState({});
  const [relationshipInfo, setRelationshipInfo] = useState([]);
  const [valuesDataType, setValuesDataType] = useState({});
  const [parsedData, setParsedData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    // Normalize and validate watchlistId before calling API
    const rawId = watchlistId;
    const normalized = typeof rawId === 'string' ? rawId.trim() : rawId;
    const isStringyNull =
      typeof normalized === 'string' &&
      (normalized.toLowerCase() === 'null' ||
        normalized.toLowerCase() === 'undefined' ||
        normalized === '');
    const isValid =
      (typeof normalized === 'string' && !isStringyNull) ||
      (typeof normalized === 'number' && !Number.isNaN(normalized));

    if (!isValid) {
      setRelatedPartyDetails({});
      setRelationshipInfo([]);
      setParsedData({});
      setValuesDataType({});
      setErrorMsg('Invalid or missing watchlist id');
      setIsLoading(false);
      return;
    }

    setErrorMsg(null);
    setIsLoading(true);
    getRelatedPartyInfo({ watchlistId: normalized })
      .then((relatedParty) => {
        // If API returns a status envelope
        if (relatedParty && relatedParty.success === false) {
          setRelatedPartyDetails({});
          setRelationshipInfo([]);
          setParsedData({});
          setValuesDataType({});
          setErrorMsg(relatedParty.msg || 'No related party found');
          return;
        }
        // Some APIs wrap payload under data
        const payload =
          relatedParty && relatedParty.data ? relatedParty.data : relatedParty;
        setRelatedPartyDetails(payload || {});
      })
      .catch((error) => {
        setErrorMsg(error?.message || 'Failed to fetch related party info');
      })
      .finally(() => setIsLoading(false));
  }, [watchlistId]);

  useEffect(() => {
    if (relatedPartyDetails?.linkedTo) {
      const linkedParties = parseJsonString(relatedPartyDetails?.linkedTo);
      setRelationshipInfo(linkedParties || []);
    }

    const dataTypeOfValue = {};
    const parsedJsonData = {};

    if (relatedPartyDetails?.passports) {
      parsedJsonData.passports = parseJsonString(
        relatedPartyDetails?.passports
      );
      dataTypeOfValue.passports = getDatatype(parsedJsonData.passports);
    }
    if (relatedPartyDetails?.companies) {
      parsedJsonData.companies = parseJsonString(
        relatedPartyDetails?.companies
      );
      dataTypeOfValue.companies = getDatatype(parsedJsonData.companies);
    }
    if (relatedPartyDetails?.pepRoles) {
      parsedJsonData.pepRoles = parseJsonString(relatedPartyDetails?.pepRoles);
      dataTypeOfValue.pepRoles = getDatatype(parsedJsonData.pepRoles);
    }
    setValuesDataType(dataTypeOfValue);
    setParsedData(parsedJsonData);
  }, [relatedPartyDetails]);

  return (
    <Modal
      title={
        <div>
          <span className="modalTitle">
            {hitDetails?.watchlistName} Alert Hit
          </span>
          <div className="hitLevelandScoreBlock">
            {hitDetails?.hitLevelLabel || capitalize(hitDetails?.hitLevel)}{' '}
            {hitDetails?.hitScore?.toFixed(2).toString()}
            {hitDetails?.hitScore && (
              <Progress
                percent={hitDetails?.hitScore}
                strokeColor={hitDetails?.hitLevelColor}
                showInfo={false}
              />
            )}
          </div>
          <Collapse ghost className="moreInfoCollapse">
            <Panel header="View more" key="1">
              <Descriptions
                size="small"
                bordered
                column={1}
                className="hitUserDetails">
                <Item label="Name:">{hitDetails?.watchlistName || '-'}</Item>
                <Item label="Alias:">{hitDetails?.watchlistAlias || '-'}</Item>
                <Item label="Gender:">
                  {hitDetails?.watchlistGender || '-'}
                </Item>
                <Item label="Date of Birth:">
                  {hitDetails?.dobWatchlist || '-'}
                </Item>
                <Item label="Nationality:">
                  {hitDetails?.watchlistNationality || '-'}
                </Item>
                <Item label="Type:">{hitDetails?.watchlistType || '-'}</Item>
              </Descriptions>
            </Panel>
          </Collapse>
        </div>
      }
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width="60%"
      wrapClassName={styles.relatedPartyModal}>
      <Spin spinning={isLoading}>
        {(() => {
          if (errorMsg) {
            return <Alert message={errorMsg} type="error" showIcon />;
          }

          if (isLoading) {
            return (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={'Loading...'}
              />
            );
          }

          return (
            <>
              <div className="partyDetailsTitle">
                Related Party / Close Associate Details
              </div>
              <Descriptions
                size="small"
                bordered
                column={2}
                className="partyDetails">
                <Item label="Party ID">
                  {relatedPartyDetails?.partyKey || hitDetails?.partyKey || '-'}
                </Item>
                <Item label="Watchlist Category">
                  {relatedPartyDetails?.watchlistCategory || '-'}
                </Item>
                <Item label="Watchlist Subcategory">
                  {relatedPartyDetails?.watchlistSubCategory || '-'}
                </Item>
                <Item label="Watchlist Name">
                  {relatedPartyDetails?.name ||
                    hitDetails?.nameOfWatchlist ||
                    '-'}
                </Item>
                <Item label="Keywords">
                  {relatedPartyDetails?.keywords || '-'}
                </Item>
                <Item label="External Sources">
                  {relatedPartyDetails?.externalSources || '-'}
                </Item>
                <Item label="Name">
                  {relatedPartyDetails?.watchlistName || '-'}
                </Item>
                <Item label="Alias">
                  {relatedPartyDetails?.watchlistAlias || '-'}
                </Item>
                <Item label="Address">
                  {relatedPartyDetails?.watchlistLocation || '-'}
                </Item>
                <Item label="Gender">
                  {relatedPartyDetails?.watchlistGender || '-'}
                </Item>
                <Item label="Birth/Inception Date">
                  {relatedPartyDetails?.dobWatchlist || '-'}
                </Item>
                <Item label="Nationality">
                  {relatedPartyDetails?.watchlistNationality || '-'}
                </Item>
                <Item label="Country of Birth/Inception">
                  {relatedPartyDetails?.watchlistCountryOfBirth || '-'}
                </Item>
                <Item label="Country of Residence">
                  {relatedPartyDetails?.watchlistCountryOfResidence || '-'}
                </Item>
                <Item label="Identification Number">
                  {relatedPartyDetails?.watchlistIdentificationNumber || '-'}
                </Item>
                <Item label="Type">
                  {relatedPartyDetails?.watchlistIdentificationType || '-'}
                </Item>
                <Item label="Title">{relatedPartyDetails?.title || '-'}</Item>
                <Item label="Watchlist Position">
                  {relatedPartyDetails?.watchlistPosition || '-'}
                </Item>
                <Item label="Place of Birth">
                  {relatedPartyDetails?.placeOfBirth || '-'}
                </Item>
                <Item label="Deceased Flag">
                  {(() => {
                    const v = relatedPartyDetails?.deceasedFlag;
                    if (v === true || v === false) {
                      return renderBooleanValue(v);
                    }
                    if (typeof v === 'string') {
                      const vs = v.toLowerCase();
                      if (vs === 'true' || vs === 'false') {
                        return renderBooleanValue(vs === 'true');
                      }
                    }
                    return v ?? '-';
                  })()}
                </Item>
                {valuesDataType?.passports !== 'array' &&
                  valuesDataType?.passports !== 'object' && (
                    <Item label="Passports">
                      {relatedPartyDetails?.passports || '-'}
                    </Item>
                  )}
                <Item label="SSN">{relatedPartyDetails?.ssn || '-'}</Item>
                {valuesDataType?.companies !== 'array' &&
                  valuesDataType?.companies !== 'object' && (
                    <Item label="Companies">
                      {relatedPartyDetails?.companies || '-'}
                    </Item>
                  )}
                <Item label="Watchlist Identification Country">
                  {relatedPartyDetails?.watchlistIdentificationCountry || '-'}
                </Item>
                {valuesDataType?.pepRoles !== 'array' &&
                  valuesDataType?.pepRoles !== 'object' && (
                    <Item label="PEP Roles">
                      {relatedPartyDetails?.pepRoles || '-'}
                    </Item>
                  )}
                <Item label="PEP Status">
                  {relatedPartyDetails?.pepStatus || '-'}
                </Item>
                <Item label="Watchperson Race">
                  {relatedPartyDetails?.watchpersonRace || '-'}
                </Item>
                <Item label="Watchlist Source">
                  {relatedPartyDetails?.watchlistSource || '-'}
                </Item>
                <Item label="Watchperson Birth Address">
                  {formatAddress(
                    relatedPartyDetails?.watchpersonBirthAddress
                  ) || '-'}
                </Item>
                <Item label="Watchlist Individual Corporate Type">
                  {relatedPartyDetails?.watchlistIndividualCorporateType || '-'}
                </Item>
                <Item label="Watchperson Residential Address">
                  {formatAddress(
                    relatedPartyDetails?.watchpersonResidentialAddress
                  ) || '-'}
                </Item>
              </Descriptions>
              {valuesDataType?.passports === 'object' &&
                renderObject(
                  parsedData.passports,
                  'Passport Details',
                  'partyDetails'
                )}
              {valuesDataType?.companies === 'object' &&
                renderObject(
                  parsedData.companies,
                  'Company Details',
                  'partyDetails'
                )}
              {valuesDataType?.pepRoles === 'object' &&
                renderObject(parsedData.pepRoles, 'PEP Roles', 'partyDetails')}
              {valuesDataType?.passports === 'array' &&
                renderTable(parsedData.passports, 'Passport Details')}
              {valuesDataType?.companies === 'array' &&
                renderTable(parsedData.companies, 'Company Details')}
              {valuesDataType?.pepRoles === 'array' &&
                renderTable(parsedData.pepRoles, 'PEP Roles')}
              <div id="collapseSection" style={{ margin: '16px 0px' }}>
                <Collapse ghost>
                  <Panel header="Relationship Information" key="1">
                    <Table
                      columns={COLUMNS || []}
                      dataSource={relationshipInfo || []}
                      pagination={relationshipInfo?.length > 10}
                      size="small"
                    />
                  </Panel>
                  <Panel header="Further Information" key="2">
                    <FurtherInformation
                      isFromRelatedParty={true}
                      relatedPartyFurtherInfo={
                        relatedPartyDetails?.furtherInformation
                      }
                    />
                  </Panel>
                </Collapse>
              </div>
            </>
          );
        })()}
      </Spin>
    </Modal>
  );
};

RelatedPartyModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  hitDetails: PropTypes.object.isRequired,
  watchlistId: PropTypes.string.isRequired
};

export default RelatedPartyModal;
